import { Flex, IconButton, Spinner, Text } from '@chakra-ui/react';
import { AiFillFile } from 'react-icons/ai';
import { BsArrowRepeat, BsExclamationTriangleFill } from 'react-icons/bs';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import {
    FileStatusType,
    MAX_FILE_SIZE,
} from '../../canopy-ui-components/api/documents';
import CanopyTooltip from '../../canopy-ui-components/components/CanopyTooltip/CanopyTooltip';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';

const FileStatus: Record<string, Record<string, any>> = {
    [FileStatusType.VALIDATION_SUCCESS]: {
        message: 'ui.mm.file-upload.ready-to-upload',
        color: 'green.500',
        icon: <IoIosCheckmarkCircle />,
    },
    [FileStatusType.ANONYMIZE_SUCCESS]: {
        message: 'ui.mm.file-upload.anonymize-success',
        color: 'green.500',
        icon: <IoIosCheckmarkCircle />,
    },
    [FileStatusType.ANONYMIZE_VALIDATION_SUCCESS]: {
        message: 'ui.mm.file-upload.ready-to-anonymize',
        color: 'brand.500',
        icon: <IoIosCheckmarkCircle />,
    },
    [FileStatusType.VALIDATION_ERROR]: {
        message: 'ui.mm.file-upload.invalid-file',
        color: 'errorText',
        icon: <BsExclamationTriangleFill />,
    },
    [FileStatusType.ANONYMIZE_ERROR]: {
        message: 'ui.mm.file-upload.password-incorrect',
        color: 'errorText',
        icon: <BsExclamationTriangleFill />,
    },
    [FileStatusType.IN_PROGRESS]: {
        message: 'ui.mm.file-upload.in-progress',
        color: 'brand.500',
        icon: 'spinner',
    },
    [FileStatusType.VALIDATION_IN_PROGRESS]: {
        message: 'ui.mm.file-upload.in-progress',
        color: 'brand.500',
        icon: 'spinner',
    },
    [FileStatusType.FILE_READING_ERROR]: {
        message: 'an-error-occurred',
        color: 'errorText',
        icon: <BsExclamationTriangleFill />,
    },
    [FileStatusType.FILE_SIZE_EXCEEDED]: {
        message: 'file-size-exceeded',
        color: 'errorText',
        icon: <BsExclamationTriangleFill />,
    },
    [FileStatusType.UPLOAD_SUCCESS]: {
        message: 'ui.mm.file-upload.ready-to-anonymize',
        color: 'green.500',
        icon: <IoIosCheckmarkCircle />,
    },
    [FileStatusType.UPLOAD_ERROR]: {
        message: 'an-error-occurred',
        color: 'errorText',
        icon: <BsExclamationTriangleFill />,
    },
    [FileStatusType.CREATE_SUCCESS]: {
        message: 'ui.mm.file-upload.in-progress',
        color: 'brand.500',
        icon: 'spinner',
    },
    [FileStatusType.CREATE_ERROR]: {
        message: 'an-error-occurred',
        color: 'errorText',
        icon: <BsExclamationTriangleFill />,
    },
    [FileStatusType.SUCCESS]: {
        message: 'ui.mm.file-upload.uploaded-successfully',
        color: 'green.500',
        icon: <IoIosCheckmarkCircle />,
    },
    [FileStatusType.ANONYMIZE_SUCCESS_STATUS]: {
        message: 'ui.mm.file-upload.anonymize-success',
        color: 'green.500',
        icon: <IoIosCheckmarkCircle />,
    },
    [FileStatusType.ANONYMIZE_CONVERTING_STATUS]: {
        message: 'engine.status.converting',
        color: 'green.500',
        icon: <BsArrowRepeat />,
    },
    [FileStatusType.ERROR]: {
        message: 'an-error-occurred',
        color: 'errorText',
        icon: <BsExclamationTriangleFill />,
    },
    [FileStatusType.READY_TO_UPDATE]: {
        message: null,
        color: null,
        icon: null,
    },
    [FileStatusType.UPDATE_SUCCESS]: {
        message: 'ui.mm.file-upload.update-success',
        color: 'green.500',
        icon: <IoIosCheckmarkCircle />,
    },
    [FileStatusType.UPDATE_ERROR]: {
        message: 'ui.mm.file-upload.update-error',
        color: 'errorText',
        icon: <BsExclamationTriangleFill />,
    },
    [FileStatusType.READY_TO_VALIDATE]: {
        message: 'ui.mm.file-upload.ready-to-validate',
        color: 'brand.500',
        icon: <IoIosCheckmarkCircle />,
    },
};

function FileName({ file, globalProps: { t } }: any) {
    const currentColors = useCurrentColors();

    return (
        <Flex
            width="100%"
            minWidth={{
                lg: '300px',
                md: '200px',
                sm: '200px',
            }}
            direction="column"
        >
            <Flex
                width="100%"
                justify="space-between"
            >
                <Flex
                    width="100%"
                    minWidth="30px"
                >
                    <Flex
                        height="14px"
                        mt="2px"
                        ml="4px"
                    >
                        <AiFillFile
                            aria-label="file-name"
                            width="14px"
                            height="14px"
                            color={currentColors.brand['300']}
                        />
                    </Flex>
                    <Flex
                        direction="column"
                        minWidth="30px"
                        ml="7px"
                    >
                        <CanopyTooltip
                            label={file?.name}
                            minWidth="30px"
                            mb="4px"
                        >
                            {file?.name ? file?.name : '-'}
                        </CanopyTooltip>
                        {file?.size ? (
                            <Text
                                color={currentColors.brand['500']}
                                fontSize="11px"
                            >
                                {parseInt(file?.size) / 1000} KB
                            </Text>
                        ) : null}
                    </Flex>
                </Flex>
            </Flex>
            {FileStatus?.[file?.status]?.icon ? (
                <Flex
                    width="100%"
                    direction="row"
                    align="center"
                >
                    {FileStatus?.[file?.status]?.icon === 'spinner' ? (
                        <Spinner
                            mr="5px"
                            width="18px"
                            height="18px"
                            minWidth="18px"
                            minHeight="18px"
                            speed="2S"
                        ></Spinner>
                    ) : (
                        <IconButton
                            backgroundColor="transparent"
                            border="none"
                            width="20px"
                            height="20px"
                            minWidth="20px"
                            minHeight="20px"
                            variant="ghost"
                            mr="5px"
                            color={
                                currentColors?.[
                                    FileStatus?.[file?.status]?.color
                                ]
                            }
                            icon={FileStatus?.[file?.status]?.icon}
                            aria-label={file?.status}
                            _hover={{ backgroundColor: 'transparent' }}
                            _focus={{
                                border: '0px',
                                backgroundColor: 'transparent',
                                outline: 'none',
                            }}
                            _active={{ border: '0px' }}
                        ></IconButton>
                    )}
                    <Text
                        fontSize="11px"
                        color={
                            currentColors?.[FileStatus?.[file?.status]?.color]
                        }
                    >
                        {file?.message ||
                            t(FileStatus?.[file?.status]?.message, {
                                size: MAX_FILE_SIZE?.text,
                            })}
                    </Text>
                </Flex>
            ) : null}
        </Flex>
    );
}
export default FileName;
